import React, { useState } from 'react';
import classNames from 'classnames';
import { IMTTripReservingDetails } from '../../../integrations/erp/apiTypes';
import generatePathWithQueryString from '../../../urls/generatePathWithQueryString';
import { Paths } from '../../../urls/Paths';
import { t } from '../../../utils/i18n';
import { baseXUnit, breakpoints, color } from '../../../styles/theme';
import H from '../../elements/H';
import Icon from '../Icon';
import DivButton from '../../a11y/DivButton';
import FadeAnimation from '../../animations/FadeAnimation';
import HyperLink from '../../elements/HyperLink';
import {
  CmsDestination,
  CmsMemory,
  instanceOfCmsMemory
} from '../../../integrations/contentful/types';
import { LoadingState } from '../../../loading/loadable';
import Modal from '../Modal';
import Spacing from '../Spacing';
import { trackAnalyticsEvent } from '../../../analytics/analytics';
import StaticTim from '../../tim/StaticTim';
import TripNotAvailableModalContent from './TripNotAvailableModalContent';

interface PriceBoxProps {
  tripReservationData?: IMTTripReservingDetails | undefined;
  tripReservationDataLoadingState: LoadingState;
  infoButtonOnClick: () => void;
  analyticsCategory: string;
  analyticsLabel: string;
  contentfulEntry: CmsMemory | CmsDestination;
}

// TODO: How about one day trips with no over night stay?
const durationNightTexts = {
  single: t('yö alkaen'),
  multiple: t('yötä alkaen')
};

const renderIcon = () => {
  return (
    <div className="icon-container">
      <Icon name="nav-arrow-right" color="white" size={4} />
    </div>
  );
};

const renderNoTripAvailableContent = (
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>,
  analyticsCategory: string,
  analyticsLabel: string
) => {
  const onButtonClicked = () => {
    setShowModal(true);
    trackAnalyticsEvent(
      analyticsCategory,
      'Avattu "matkaa ei saatavilla" modaali auki',
      analyticsLabel
    );
  };

  return (
    <DivButton
      className="reservation-link"
      onClick={onButtonClicked}
      data-testid="price-box-no-trip-available"
      data-cy="price-box-no-reservation-available-link"
    >
      <Spacing left={2}>
        <H styling="h4" tag="div" color="white">
          {t('Ei juuri nyt saatavilla')}
        </H>
        <H styling="h3" tag="div" color="white">
          <strong>{t('Lue lisää')}</strong>
        </H>
      </Spacing>
      {renderIcon()}
    </DivButton>
  );
};

const renderTripAvailableContent = (
  tripReservationData: IMTTripReservingDetails,
  analyticsCategory: string,
  analyticsLabel: string,
  contentfulEntry: CmsMemory | CmsDestination
) => {
  const { durationAsNights, pricePerPassenger } = tripReservationData;
  const reservationPath = getReservationPathFromTripPriceResponse(
    tripReservationData.tripId,
    tripReservationData.date,
    tripReservationData.extendableHotelPhaseNights,
    contentfulEntry
  );
  const secondaryText = `
    ${durationAsNights} ${t(
    durationNightTexts.single,
    durationNightTexts.multiple,
    durationAsNights > 1
  )} ${pricePerPassenger} ${t('€/hlö')}`;

  return (
    <HyperLink
      href={reservationPath}
      hrefEventData={{
        category: analyticsCategory,
        action: 'Klikattu matkan varauspainiketta',
        label: analyticsLabel,
        value: pricePerPassenger
      }}
      className="reservation-link"
      data-cy="reservation-link"
      data-testid="price-box-trip-available"
      noLinkStateStyles
    >
      <Spacing left={2}>
        <H styling="h3" tag="div" color="white">
          <strong>{t('Varaa matka')}</strong>
        </H>
        <H styling="h4" tag="div" color="white" data-cy="duration-and-price">
          <strong>{secondaryText}</strong>
        </H>
      </Spacing>
      {renderIcon()}
    </HyperLink>
  );
};

const PriceBox: React.FunctionComponent<PriceBoxProps> = ({
  tripReservationData,
  tripReservationDataLoadingState,
  infoButtonOnClick,
  analyticsCategory,
  analyticsLabel,
  contentfulEntry
}) => {
  const [showNoTripAvailableModal, setShowNoTripAvailableModal] =
    useState<boolean>(false);
  if (tripReservationDataLoadingState === LoadingState.STARTED) {
    return <></>;
  }
  return (
    <FadeAnimation>
      <div className="container" data-cy="price-box">
        <div className="content">
          <DivButton
            data-testid="info-button"
            data-cy="travel-info-button"
            onClick={() => {
              if (tripReservationData) {
                infoButtonOnClick();
              } else {
                setShowNoTripAvailableModal(true);
              }
            }}
          >
            <div
              className={classNames('icon-container', 'info-icon-border', {
                'no-trip-available': !tripReservationData
              })}
            >
              {!tripReservationData && <StaticTim size={[4, 4, 6]} />}
              {tripReservationData && (
                <Icon name="travel-info" size={[4, 4, 6]} color="white" />
              )}
            </div>
          </DivButton>
          {tripReservationData
            ? renderTripAvailableContent(
                tripReservationData,
                analyticsCategory,
                analyticsLabel,
                contentfulEntry
              )
            : renderNoTripAvailableContent(
                setShowNoTripAvailableModal,
                analyticsCategory,
                analyticsLabel
              )}
        </div>
      </div>
      <Modal
        isOpen={showNoTripAvailableModal}
        onDismiss={() => {
          setShowNoTripAvailableModal(false);
        }}
        ariaLabel={t('Tim')}
        variant="right"
        animateIn
      >
        <TripNotAvailableModalContent
          close={() => {
            setShowNoTripAvailableModal(false);
          }}
          memoryOrDestinationTitle={contentfulEntry.fields.title}
          analyticsCategory={analyticsCategory}
        />
      </Modal>
      {/*language=CSS*/}
      <style jsx>{`
        .container {
          position: relative;
          z-index: 10;
          min-height: ${baseXUnit(9)};
          width: 100%;
          background-color: ${color.white};
          box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
          padding: ${baseXUnit(2)} ${baseXUnit(1)};
          background-color: ${color.purple};
          transition: 0.5s;
        }
        .container:hover {
          background-color: ${color.purpleLight};
        }
        .content {
          display: flex;
          color: ${color.white};
        }
        .content :global(.reservation-link) {
          display: flex;
          justify-content: space-between;
          width: 100%;
        }
        :global(.icon-container) {
          display: flex;
          align-items: center;
          opacity: 0.7;
          height: ${baseXUnit(5)};
        }
        :global(.icon-container:hover) {
          opacity: 1;
        }
        .info-icon-border {
          ${tripReservationData
            ? `padding-right: ${baseXUnit(1)};` +
              `border-right: 1px dashed ${color.light};`
            : ''}
        }
        .info-icon-border.no-trip-available {
          opacity: 1;
        }
        .text-content {
          margin-left: ${baseXUnit(2)};
        }

        @media ${breakpoints.extraLarge} {
          .container {
            padding: ${baseXUnit(1.5)} ${baseXUnit(2)};
            min-height: ${baseXUnit(10)};
          }
          :global(.icon-container) {
            height: 100%;
          }
        }
      `}</style>
    </FadeAnimation>
  );
};

/**
 * Build a path to the reservation page which contains proper query parameters required
 * by the reservation pipeline to initiate reservation.
 * @param imtTripId
 * @param date
 * @param extendableHotelPhaseNights
 */
function getReservationPathFromTripPriceResponse(
  imtTripId: number,
  date: string,
  extendableHotelPhaseNights: number | null,
  contentfulEntry: CmsDestination | CmsMemory
) {
  const extendableHotelPhaseNightsParam = extendableHotelPhaseNights
    ? extendableHotelPhaseNights.toString()
    : undefined;

  return generatePathWithQueryString(Paths.CreateReservation, {
    departureDate: date,
    extendableHotelPhaseNights: extendableHotelPhaseNightsParam,
    trip: String(imtTripId),
    timProductId: getTimProductId(contentfulEntry),
    timProductName: contentfulEntry.fields.title,
    timProductCategory: getTimProductCategory(contentfulEntry)
  });
}

function getTimProductId(contentfulEntry: CmsDestination | CmsMemory) {
  if (instanceOfCmsMemory(contentfulEntry)) {
    return 'CM-' + contentfulEntry.sys.id; // CM for Contentful Memory
  } else {
    return 'CD-' + contentfulEntry.sys.id; // CD for Contentful Destination
  }
}

function getTimProductCategory(contentfulEntry: CmsDestination | CmsMemory) {
  if (instanceOfCmsMemory(contentfulEntry)) {
    return 'Lomamuistot';
  } else {
    return 'Matkakohteet';
  }
}

export default PriceBox;
